import styled from '@emotion/native';
import { getCreateReferenceId } from '@quromedical/fhir-common';
import type { BaseTimeSeriesRecord } from '@quromedical/models';
import { minby, orderby } from '@quromedical/utils';
import { Col, Row } from 'components/base';
import { Icon, Notification, Spacer, Text } from 'design-system';
import { BASE_SIZE, BORDER_WIDTH, MARGIN, PADDING } from 'design-system/theme';
import { router } from 'expo-router';
import type React from 'react';
import { useCallback } from 'react';
import { strings } from 'strings';

import { useUrgentReviewList } from './clinical-review';
import { getNEWS2Display, news2RiskColorMap } from './news';

const Wrapper = styled.View({
  marginVertical: MARGIN['2xs'],
  marginHorizontal: MARGIN.xs,
});

const Title = styled(Row)(({ theme }) => ({
  borderColor: theme.color.divider,
  borderStyle: 'solid',
  borderWidth: 0,
  borderTopWidth: BORDER_WIDTH[1],
  paddingTop: PADDING.xs,
  paddingHorizontal: PADDING.xs,
  marginBottom: MARGIN['2xs'],
  marginHorizontal: MARGIN.xs,
}));

export const SidebarAlerts: React.FC = () => {
  const onPatientPress = useCallback(
    (id: string) => router.push({ pathname: `/patient/${id}/notes` }),
    [],
  );

  const { admissionRisks } = useUrgentReviewList(true);

  const sortedItems = orderby(
    admissionRisks,
    (item) => item.news.score,
    'desc',
  );

  return (
    <Col>
      <Title alignItems="center" justifyContent="flex-start">
        <Icon name="alarm" size={BASE_SIZE[16]} color="text-subdued" />
        <Spacer width={MARGIN.xs} />
        <Text color="text-subdued" variant="caption">
          {strings.TitleUrgentClinicalReviewCritical}
        </Text>
      </Title>

      {sortedItems.map((item) => {
        const { admission, news, vitals } = item;
        const { patient } = admission;

        const id = getCreateReferenceId(patient);
        const display = patient.display || strings.DisplayNotFound;

        const backgroundColor = news2RiskColorMap[news.risk];
        const title = getNEWS2Display(news, true);

        const latestMeasures = Object.values<BaseTimeSeriesRecord | undefined>(
          vitals.latest || {},
        );

        // use the oldest time to indicate when the measurement was taken
        const time = minby(latestMeasures, (v) => v?.ts)?.ts;

        return (
          <Wrapper key={id}>
            <Notification
              key={id}
              icon="circle"
              title={title}
              body={display}
              color={backgroundColor}
              time={time}
              onPress={() => onPatientPress(id)}
            />
          </Wrapper>
        );
      })}
    </Col>
  );
};
